import { Button } from '@material-ui/core';
import React from 'react';
import {
  IP_LOOKUP_BASE_URL,
  TIME_PROPERTY_REGEX,
  ISO_DATE_REGEX,
} from '../../constants';
import { PropertyFormat } from '../../types';
import {
  DATE_TRADITIONAL_FORMAT,
  format,
  TIME_SECONDS_AM_PM_ALT_FORMAT,
} from '../../util/dateFns';
import SafeURL from '../SafeURL';

export default function formatProperty(
  format: PropertyFormat,
  val?: any,
  classes?: { webLinkButton: any; webLinkText: any }
) {
  if (val === null || val === undefined) {
    val = '';
  }
  switch (format) {
    case 'uri':
      const linkLength = (val as string).length;
      const linkText =
        linkLength > 42
          ? (val as string).substring(0, 18) +
            '…' +
            (val as string).substring(linkLength - 12)
          : val;
      return classes ? (
        <Button
          variant="text"
          color="primary"
          classes={{
            root: classes.webLinkButton,
            label: classes.webLinkText,
          }}
        >
          <SafeURL url={val} displayText={linkText} />
        </Button>
      ) : (
        <SafeURL url={val} displayText={linkText} />
      );
    case 'email':
      const mailto = `mailto:${val}`;
      return classes ? (
        <Button
          variant="text"
          color="primary"
          classes={{
            root: classes.webLinkButton,
            label: classes.webLinkText,
          }}
        >
          <SafeURL url={mailto} displayText={val} />
        </Button>
      ) : (
        <SafeURL url={mailto} displayText={val} />
      );
    case 'ip':
    case 'ipv4':
    case 'ipv6':
      if (typeof val !== 'string') {
        return classes ? (
          <Button
            variant="text"
            disabled
            classes={{
              root: classes.webLinkButton,
              label: classes.webLinkText,
            }}
          >
            {val}
          </Button>
        ) : (
          <span>{val}</span>
        );
      }

      const link = `${IP_LOOKUP_BASE_URL}${val.split('/')[0]}`;
      return classes ? (
        <Button
          variant="text"
          color="primary"
          classes={{
            root: classes.webLinkButton,
            label: classes.webLinkText,
          }}
        >
          <SafeURL url={link} displayText={val} />
        </Button>
      ) : (
        <SafeURL url={link} displayText={val} />
      );
    default:
      return formatDate(val, format);
  }
}

// Invalid Date is hard to catch, instead return valid dates or undefined
export const sanitizeDate = (value: string | number): Date | undefined => {
  try {
    if (value === null || value === undefined || typeof value === 'boolean') {
      return;
    }

    let parsedDate: Date;
    const ISODateRegex = ISO_DATE_REGEX;
    if (typeof value === 'string' && ISODateRegex.exec(value)) {
      parsedDate = new Date(value);
    } else {
      const dateNumber = parseInt(value as string, 10);
      parsedDate = new Date(
        dateNumber > 2000000000 ? dateNumber : dateNumber * 1000 // Converts date number in seconds to milliseconds
      );
    }

    if (parsedDate?.getTime()) return parsedDate;
    return; // invalid date
  } catch {
    return;
  }
};

export function formatDate(value: any, format: string) {
  const parsedDate = sanitizeDate(value);
  try {
    switch (format) {
      case 'date-time':
        if (!parsedDate) return value && value.toString();
        return parsedDate.toISOString();
      case 'utc-date':
        if (!parsedDate) return value && value.toString();
        return parsedDate.toUTCString();
      case 'iso-date':
        if (!parsedDate) return value && value.toString();
        return parsedDate.toISOString();
      case 'date':
        if (!parsedDate) return 'Invalid Date';
        return formatDateTime(parsedDate);
      case 'local-time':
        if (!parsedDate) return 'Invalid Date';
        return formatDateTime(parsedDate, true);
      default:
        return value && value.toString();
    }
  } catch (error) {
    return value && value.toString();
  }
}

export function formatDateTime(date: Date, includeTime?: boolean) {
  const dateFormat = DATE_TRADITIONAL_FORMAT;
  const timeFormat = ' ' + TIME_SECONDS_AM_PM_ALT_FORMAT;
  const formatString = `${dateFormat}${includeTime ? timeFormat : ''}`;
  try {
    return format(date, formatString);
  } catch (error) {
    return 'Invalid Date';
  }
}

export function formatPropertyBasedOnNameValue(
  name: string,
  value: any,
  classes?: any
) {
  if (value === null || value === undefined) {
    value = '';
  }
  const textFields = ['name', 'displayName', 'description'];
  const costPropertyRegex = /cost|dollar/i;
  if (textFields.includes(name) || name.startsWith('_')) {
    return value.toString();
  } else if (TIME_PROPERTY_REGEX.exec(name) !== null) {
    TIME_PROPERTY_REGEX.lastIndex = 0;
    return formatDate(value, 'iso-date');
  } else if (typeof value === 'string') {
    const valueLC = value.toLowerCase();
    const uriRegex = /^https?:\/\/.*/;
    const ipv4Regex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}(\/[0-9]{1,2})?$/;

    if (uriRegex.exec(valueLC) !== null) {
      return formatProperty('uri', value, classes);
    } else if (ipv4Regex.exec(value) !== null) {
      return formatProperty('ip', value, classes);
    } else {
      return value.toString();
    }
  } else if (
    costPropertyRegex.exec(name) !== null &&
    typeof value === 'number'
  ) {
    return value.toFixed(2).toString();
  } else {
    return value.toString();
  }
}
